/**
 * DEFUSE S10 — the INSERT transform TRIPLE and the primitives every other
 * block-flatten module is built on: the point / direction maps, the scalar
 * angle-sum convention, and the degree/radian helpers they share.
 *
 * Split out of `./blockTransform.ts` (which re-exports the public surface and
 * carries the family's module documentation) purely for file size — the math,
 * the conventions and the byte-exact expressions are unchanged. This is the
 * LEAF of the family: it imports nothing from its siblings, so
 * `./blockArcEllipseMapping.ts`, `./blockChildIdentity.ts`,
 * `./transformBlockChild.ts`, `./blockDimensionPrimitive.ts`,
 * `./blockHatchEdge.ts` and `./blockBulgeExplode.ts` can all depend on it
 * without a cycle.
 */

import type { BlockLinearRsr } from "@dxf-ai-editor/cad-core";

import type { Vec3 } from "../../types";

export const TWO_PI = Math.PI * 2;

/**
 * Degrees → radians with the LEGACY operation order `(deg · π) / 180`
 * (`blockInsert.ts:281`). The order matters: `deg · (π/180)` can differ by an
 * ulp, and the golden-parity harness compares the flatten outputs bit-exactly.
 */
export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * S10 baseline INSERT transform triple. Deliberately parameterised the way
 * the legacy flatten accumulates it (insertion + per-axis scale + 2D rotation
 * in DEGREES), NOT as a general matrix: nested INSERTs compose by
 * `scale.x·child.scaleX / rotation + child.rotation` exactly like legacy
 * `flattenBlockInsert:746-751` (plan pins this accumulation as the S10
 * baseline; the full Mat4 compose is the S10-XFORM delta).
 *
 * The OPTIONAL `preRotationDeg` below is the one addition to that shape: it
 * widens the LINEAR part to the SVD form `R·S·R`, which any 2D linear map has,
 * so a caller that must compose an enclosing affine onto a rotated reference
 * can do so without the shear loss the 3-DOF triple would take. It defaults to
 * absent, and every helper here falls back to the byte-identical pre-existing
 * expression when it is (the golden-parity harness compares bit-exactly).
 */
export interface BlockInsertTransform {
  /** World insertion point (raw DXF coords — the adapters apply the
   *  coordinate-origin offset once, per leaf field). */
  insertion: Vec3;
  /** Per-axis scale. Negative components mirror (AutoCAD semantics). */
  scale: Vec3;
  /** CCW rotation about Z in DEGREES (the DXF INSERT group-50 unit). */
  rotationDeg: number;
  /**
   * OPTIONAL INNER rotation in DEGREES, applied to the block-local vector
   * BEFORE the per-axis scale — i.e. the linear part is
   *
   *     L = R(rotationDeg) · S(scale.x, scale.y) · R(preRotationDeg)
   *
   * Absent / `0` ⇒ identity, and every arithmetic path below falls back to
   * the byte-identical pre-existing expression (the S10 golden-parity harness
   * compares the flatten outputs bit-exactly, so the fallback is an explicit
   * branch, never a `× cos(0)`).
   *
   * # Why the triple needs it (Codex PR #784 round 6 P2)
   *
   * `R(θ)·S(a,b)` has 3 degrees of freedom; a general 2×2 linear map has 4.
   * So a triple can NOT express the composition of an outer non-uniform
   * reference with an inner ROTATED one — `S(a,b)·R(φ)` is a genuine SHEAR
   * whenever `|a| ≠ |b|` and `φ ≢ 0 (mod 180°)`, and folding it into
   * `(rotationDeg + φ, a·s, b·s)` silently emits geometry in the wrong place
   * (Codex's case: an MLEADER whose `blockRotation` is 45° inside an INSERT
   * scaled `(3, 2)` — the content line then runs outside the very bounds
   * `cad-core`'s `blockDefinitionWalk` computes for the same INSERT, so
   * cache-direct culling and picking can miss it).
   *
   * `R·S·R` is exactly the SVD shape, so the triple-plus-inner-rotation CAN
   * express any 2D linear map, and it does so WITHOUT changing the meaning of
   * any existing field: `isMirroredTransform` (det sign), the uniform-|scale|
   * fast paths and the `|scale.x|` / `|scale.y|` magnitude conventions all
   * read `scale` alone and stay exactly as they were.
   *
   * The ONE producer today is the block-child MLEADER's block-content
   * reference (`./blockChildMultileader.ts`
   * {@link composeBlockContentReference}); everything else omits it.
   */
  preRotationDeg?: number;
}

/** `true` when the accumulated 2D linear part flips handedness (mirror). */
export function isMirroredTransform(t: BlockInsertTransform): boolean {
  return t.scale.x * t.scale.y < 0;
}

/**
 * This transform's 2D LINEAR part in `cad-core`'s shared `R·S·R` shape — the
 * bridge to the ONE composition truth (`@dxf-ai-editor/cad-core`'s
 * `blockLinearTransform.ts`), which the block-BOUNDS walk replays to reserve
 * space for what this flatten draws. The Z half never participates: it is a
 * pure scale in this stack (`transformBlockChildPoint`'s legacy z-collapse).
 */
export function blockLinearOfTransform(
  t: BlockInsertTransform,
): BlockLinearRsr {
  return {
    rotationDeg: t.rotationDeg,
    scaleX: t.scale.x,
    scaleY: t.scale.y,
    preRotationDeg: t.preRotationDeg ?? 0,
  };
}

/**
 * `cos`/`sin` of the optional inner rotation, or `null` when the transform
 * has none — the ONE place the "absent ⇒ pre-existing arithmetic verbatim"
 * fast path is decided (see {@link BlockInsertTransform.preRotationDeg}).
 */
export function preRotationCs(
  t: BlockInsertTransform,
): { cos: number; sin: number } | null {
  const pre = t.preRotationDeg;
  if (!pre) return null;
  const rad = degToRad(pre);
  return { cos: Math.cos(rad), sin: Math.sin(rad) };
}

/**
 * Effective rotation (DEGREES) for the SCALAR angle-sum convention — TEXT /
 * MTEXT rotation, Arc sweep endpoints, every "absolute angle + the INSERT
 * rotation" field. ONE truth for the three folds that make it exact:
 *
 *   - the DOUBLE-negative uniform scale is a point reflection, not a mirror
 *     (`S(−s,−s) ≡ R(180°)·S(s,s)`), so it adds 180°;
 *   - an inner rotation φ ({@link BlockInsertTransform.preRotationDeg}) adds
 *     φ, because `R(θ)·S(s,s)·R(φ) ≡ R(θ+φ)·S(s,s)`;
 *   - …with the SIGN reversed under a true mirror (det < 0): a reflection
 *     reverses the sense of the rotation it encloses,
 *     `S(−1,1)·R(φ) ≡ R(−φ)·S(−1,1)`, and the scale is applied BEFORE the
 *     outer rotation here.
 *
 * (Under a NON-uniform scale a glyph angle is not a similarity image at all —
 * that residual is the S10-XFORM glyph-shear delta, module header, identical
 * with and without an inner rotation.)
 */
export function blockScalarRotationDeg(t: BlockInsertTransform): number {
  const base =
    t.scale.x < 0 && t.scale.y < 0 ? t.rotationDeg + 180 : t.rotationDeg;
  const pre = t.preRotationDeg;
  if (!pre) return base;
  return isMirroredTransform(t) ? base - pre : base + pre;
}

/**
 * Transform a block-local point into world space — the single-step
 * composition of the legacy basePoint subtract (`offsetPoint`,
 * `blockInsert.ts:801-805`) and `transformPoint` (`:275-294`), with the
 * insertion folded in (the legacy path adds it in a separate
 * `translatePrimitives` pass purely as a cache artefact; the math is
 * identical).
 */
export function transformBlockChildPoint(
  p: { x: number; y: number; z?: number },
  basePoint: Vec3,
  t: BlockInsertTransform,
): Vec3 {
  const rad = degToRad(t.rotationDeg);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Inner rotation first (identity — and the byte-identical expression below —
  // when absent): `L = R(rot)·S(scale)·R(pre)`.
  const pre = preRotationCs(t);
  const lx0 = p.x - basePoint.x;
  const ly0 = p.y - basePoint.y;
  const lx = pre ? lx0 * pre.cos - ly0 * pre.sin : lx0;
  const ly = pre ? lx0 * pre.sin + ly0 * pre.cos : ly0;
  const sx = lx * t.scale.x;
  const sy = ly * t.scale.y;
  return {
    x: sx * cos - sy * sin + t.insertion.x,
    y: sx * sin + sy * cos + t.insertion.y,
    // Legacy z-collapse (`blockInsert.ts:292`): scaled + translated, never
    // rotated. 3D rotation + extrusion are the S10-XFORM delta.
    z: ((p.z ?? 0) - basePoint.z) * t.scale.z + t.insertion.z,
  };
}

/**
 * Transform a DIRECTION / relative vector (spline tangents, ellipse major
 * axis): the linear part `R·S` only — no basePoint subtract, no insertion
 * translate (plan §4.2 S10-XFORM: "векторы = линейная часть без translation").
 */
export function transformBlockDirection(
  v: { x: number; y: number; z?: number },
  t: BlockInsertTransform,
): Vec3 {
  const rad = degToRad(t.rotationDeg);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const pre = preRotationCs(t);
  const lx = pre ? v.x * pre.cos - v.y * pre.sin : v.x;
  const ly = pre ? v.x * pre.sin + v.y * pre.cos : v.y;
  const sx = lx * t.scale.x;
  const sy = ly * t.scale.y;
  return {
    x: sx * cos - sy * sin,
    y: sx * sin + sy * cos,
    z: (v.z ?? 0) * t.scale.z,
  };
}

export function normalizeDeg(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

export function normalizeRad(rad: number): number {
  const r = rad % TWO_PI;
  return r < 0 ? r + TWO_PI : r;
}

/** Degrees → radians with the RENDER path's constant (`DEG_TO_RAD` in
 *  `adapters/sceneGeometryBuilders.ts`), NOT {@link degToRad}, whose
 *  `(deg · π) / 180` order is pinned to the legacy flatten's arithmetic. */
export const DEG_TO_RAD = Math.PI / 180;
